// Cuerpo de la card «Inventario estancado y capital atado» de Análisis.
import { ErrorState, Skeleton } from '@/kit';
import { userMessageOf } from '@/api/errors';
import { cn } from '@/lib/cn';
import { moneyShort, plural } from '../lib/analisisMetrics';
import {
    capitalSharePct,
    type StagnantInventory,
    type StagnantSku,
} from '../lib/stagnantInventory';

/** Cuántos SKUs estancados lista la tabla; el resto se cuenta al pie. */
const STAGNANT_ROW_LIMIT = 15;

const TH = 'bg-muted/40 px-2 py-1 text-left text-[9.5px] font-bold uppercase tracking-[0.04em]';
const TD = 'border-b border-border/50 px-2 py-1 text-[10.5px]';
const NUM = 'text-right tabular-nums';

function shareLabel(pct: number | null): string {
    return pct === null ? '—' : `${pct.toLocaleString('es-MX', { maximumFractionDigits: 1 })}%`;
}

/** El kit se muestra por su rotación, pero su valor ya está en sus componentes. */
function ValueCell({ row }: { row: StagnantSku }) {
    if (row.isKit) {
        return (
            <td
                className={cn(TD, NUM, 'italic text-muted-foreground')}
                title="Su valor ya suma en las filas de sus componentes"
            >
                en componentes
            </td>
        );
    }
    return (
        <td
            className={cn(TD, NUM, 'font-semibold')}
            title={row.valueCents === null ? 'Sin precio registrado' : undefined}
        >
            {row.valueCents === null ? '—' : moneyShort(row.valueCents)}
        </td>
    );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
    return (
        <div className="rounded-[5px] border border-border px-2.5 py-2">
            <div className="text-[9.5px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </div>
            <div className="mt-0.5 text-[16px] font-semibold tabular-nums">{value}</div>
            <div className="text-[10.5px] leading-snug text-muted-foreground">{sub}</div>
        </div>
    );
}

export function StagnantInventoryPanel({
    data,
    isPending,
    error,
    onRetry,
}: {
    data: StagnantInventory | undefined;
    isPending: boolean;
    error: unknown;
    onRetry: () => void;
}) {
    if (error) {
        return (
            <ErrorState
                title="No se pudo cargar el inventario estancado"
                description={`${userMessageOf(error)} Los cortes de venta de esta pantalla no dependen del inventario.`}
                onRetry={onRetry}
                className="m-2.5"
            />
        );
    }
    if (isPending || !data) return <Skeleton lines={5} />;

    const share =
        data.tiedCapitalCents > 0
            ? Math.round((data.stagnantValueCents / data.tiedCapitalCents) * 100)
            : 0;
    const visible = data.stagnant.slice(0, STAGNANT_ROW_LIMIT);
    const hidden = data.stagnant.length - visible.length;

    return (
        <div className="flex flex-col gap-2 px-2.5">
            <div className="grid grid-cols-2 gap-2">
                <Tile
                    label="Capital atado"
                    value={moneyShort(data.tiedCapitalCents)}
                    sub="Stock por precio vigente, todas las tiendas"
                />
                <Tile
                    label="Estancado"
                    value={moneyShort(data.stagnantValueCents)}
                    sub={`${plural(data.stagnant.length, 'SKU', 'SKUs')} · ${String(share)}% del capital`}
                />
            </div>

            {data.unpricedSkus > 0 ? (
                <p className="text-[10px] leading-snug text-muted-foreground">
                    {plural(
                        data.unpricedSkus,
                        'SKU con stock no tiene',
                        'SKUs con stock no tienen',
                    )}{' '}
                    precio registrado y no suma al capital.
                </p>
            ) : null}

            {data.stagnant.length === 0 ? (
                <p className="p-3 text-center text-[11px] italic leading-relaxed text-muted-foreground">
                    Todos los SKUs con stock se vendieron al menos una vez en el periodo.
                </p>
            ) : (
                <table className="w-full table-fixed border-collapse text-[10.5px]">
                    <thead>
                        <tr>
                            <th scope="col" className={cn(TH, 'w-[34%]')}>
                                Producto
                            </th>
                            <th scope="col" className={TH}>
                                SKU
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Stock
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                Valor
                            </th>
                            <th scope="col" className={cn(TH, 'text-right')}>
                                % del capital
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((row) => (
                            <tr key={row.sku}>
                                <td className={TD}>
                                    <div className="truncate" title={row.label}>
                                        {row.label}
                                    </div>
                                </td>
                                <td className={cn(TD, 'truncate text-muted-foreground')}>
                                    {row.sku}
                                </td>
                                <td className={cn(TD, NUM)}>{row.stock}</td>
                                <ValueCell row={row} />
                                <td className={cn(TD, NUM)}>
                                    {shareLabel(
                                        capitalSharePct(row.valueCents, data.tiedCapitalCents),
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {hidden > 0 ? (
                <p className="text-[10px] text-muted-foreground">
                    y {plural(hidden, 'SKU estancado más', 'SKUs estancados más')}, de menor valor.
                </p>
            ) : null}
        </div>
    );
}
